/* =====================================================================
   Alex & Lace — hero scroll choreography (Pass 2 / Phase 8).
   GSAP + ScrollTrigger.

   CONSTRAINT (spec): position:fixed is forbidden on hero elements — WITH ONE
   scoped exception (owner-approved 2026-07-15, CLAUDE.md + al-hero-score.md P2):
   the CTA BUTTON alone may use a real GSAP pin (its pin-spacer/fixed mechanism).
   The old jank was position:fixed detaching CONTENT during scroll (the product
   strip floated onto the footer); a GSAP pin scoped to the single decoupled
   button, with a runtime-clamped end, does not reintroduce it.
     - image        → slower parallax transform (background, lags DOWN) + a
                      one-shot entrance zoom-out (1.15→1, driven by the curtain)
     - headline     → faster parallax transform (foreground, leads UP), retimed
                      to RESOLVE at the pin release (EXIT DEAD-ZONE fix) — races up
                      through the hold, ease-out settles as the button un-pins
     - Shop-Now blk → NOT parallaxed; scrolls normally
     - CTA button   → the decoupled right button: real GSAP pin (a brief BEAT then
                      cleanly un-pins) + scale-through-travel 0.9→1.0
     - footer band  → parallax dock, flush by the band-top crossing ~50vh
     - footer cards → ease-IN reveal as they enter (desktop-only by markup)
   The hero SECTION itself is NEVER pinned; it scrolls away normally.
   Values are LOCKED (defaults-lock 2026-07-17) — preview axes stripped.

   Gating: desktop + prefers-reduced-motion:no-preference only, via
   gsap.matchMedia() — which auto-reverts every transform/pin/trigger below
   when the query stops matching (resize to mobile, or reduced-motion on).
   ===================================================================== */

(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;

  // Entrance-curtain handshake (score P0): resolve once our ScrollTriggers are bound (or there is
  // nothing to bind — no gsap, no hero, or mobile/reduced-motion), so the curtain keeps the
  // parallax/pin's first frame covered. The curtain reads window.AL_ST_READY.
  if (!window.AL_ST_READY) window.AL_ST_READY = new Promise((res) => { window.__alStReadyResolve = res; });
  const resolveST = () => { if (window.__alStReadyResolve) { window.__alStReadyResolve(); window.__alStReadyResolve = null; } };

  if (!gsap || !ScrollTrigger) { resolveST(); return; }

  const hero = document.querySelector('[data-al-hero]');
  if (!hero) { resolveST(); return; }

  gsap.registerPlugin(ScrollTrigger);

  /* ---- Tunables (feel) — LOCKED (defaults-lock 2026-07-17) ----------------
     Image lags DOWN, headline leads UP, across the hero's scroll-out. The differential
     IS the depth. scrub = seconds of smoothing lag.
     The HEADLINE is still the locked 'mid' preset (−0.19, a fraction of the VIEWPORT).
     The IMAGE is NO LONGER a `?para` preset: as of 2026-07-30 it is a RATE against the
     HERO's own height (About parity — see §1), which is why the two carry different units. */
  const CFG = {
    // IMAGE DRIFT — RATE, not a vh amplitude (About-parity pass 2026-07-30; supersedes the
    // defaults-lock value `imageDrift: 0.115` = +11.5%vh). See §1 for the full rationale.
    imageDriftRate: 0.189, // image → +18.9% of HERO HEIGHT down = 0.189 px/px, at every viewport
    headlineLead: -0.19, // headline → -19%vh   up   (mid)
    scrub:        0.9,   // smoothing seconds
    cardsDur:     0.7,
    cardsStagger: 0.12,
  };

  // LOCKED: CTA at the RIGHT placement (decoupled from the Shop-Now block; CSS scopes it to
  // >=1024, so mobile uses the static inline flow). Class applied unconditionally.
  hero.classList.add('al-ctapos--right');

  // LOCKED: CTA scale-through-travel starts at 0.9 ('subtle') and scrubs to 1.0 by pin release.
  const CTA_START_SCALE = 0.9;

  const vh = () => window.innerHeight;
  const heroRange = () => ({ trigger: hero, start: 'top top', end: 'bottom top' });

  const mm = gsap.matchMedia();

  mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
    const img = hero.querySelector('[data-al-hero-img]');
    const headline = hero.querySelector('.al-hero__headline-wrap');
    const footer = hero.querySelector('[data-al-hero-footer]');
    const cards = footer ? footer.querySelectorAll('.al-hero__fcard') : [];
    const ctaBtn = hero.querySelector('[data-al-hero-cta]');
    const shopnow = hero.querySelector('[data-al-hero-shopnow]');

    // SHARED PIN-RELEASE POINT (hoisted). The CTA pin AND the CTA scale resolve at this scroll
    // distance; the headline lead uses it only as a FLOOR (it ends at its own viewport exit — see 2).
    //
    // NEW END RULE (2026-07-18, owner decision — SUPERSEDES both the old footer-derived end AND the
    // dead-zone 0.36·vh cap): the pin releases when the SHOPNOW block's TOP edge aligns with the HELD
    // BUTTON's TOP edge, so on release they scroll off together as one row (tops flush). Geometry: the
    // pin holds the button at its scroll-0 screen top (= ctaBtn.offsetTop); the shopnow block rides up
    // in flow (screen top = shopnow.offsetTop − scroll); alignment ⇒ scroll = shopnow.offsetTop −
    // ctaBtn.offsetTop. Both resolve offsetTop against .al-hero__content, so the subtraction is exact.
    // Live geometry + invalidateOnRefresh → self-adapting per viewport. NO hardcoded distance, NO VH cap.
    //   FLOOR (short / very-tall viewports where the shopnow block sits at/above the button's rest, so
    //     the computed end would be ≤ 0): hold a minimal BEAT (0.15·vh) instead of instant-releasing —
    //     never negative, never zero-hold.
    //   CEILING (safety, well ABOVE the computed end — repurposed from the old runway, not a design cap):
    //     never hold past the point the button would strand BELOW the hero's bottom on release; i.e.
    //     release while still over the hero. heroH − (btnTop + btnH) − 20.
    const pinEnd = () => {
      const btnTop = ctaBtn ? ctaBtn.offsetTop : 0;
      const btnH = ctaBtn ? ctaBtn.offsetHeight : 0;
      // aligned = the shopnow-top ↔ button-top alignment scroll. Fallback (no shopnow block): a moderate beat.
      const aligned = shopnow ? shopnow.offsetTop - btnTop : 0.33 * vh();
      const floor = 0.15 * vh();
      const ceiling = hero.offsetHeight - (btnTop + btnH) - 20;
      return Math.max(0, Math.min(Math.max(aligned, floor), ceiling));
    };

    // 1. Image parallax — lags DOWN across the full hero exit. RANGE UNCHANGED (owner — "image stays
    //    settled"; it's the subtle backdrop and mostly off-screen by the hold, so it keeps its range).
    //
    //    AMPLITUDE RAISED TO ABOUT PARITY (owner spec 2026-07-30, SUPERSEDES the defaults-lock
    //    `imageDrift: 0.115`). The About hero glides at 0.189 px/px (306px over its 1620px runway —
    //    al-about-hero-motion.js); this one ran at 0.0778 px/px (103.5px over the 1331px hero @1440×900),
    //    2.43× slower. Two heroes, two different drift feels — the pronounced glide is the cohesion cue,
    //    so this one now matches it exactly.
    //
    //    WHY THE REFERENCE LENGTH CHANGED (vh → hero height), and why that is NOT a mechanic change:
    //    "0.189 px/px" is only a CONSTANT if the amplitude is measured in the same unit as the scroll
    //    range. About can use vh because its section is a fixed 180vh, so vh and its range are the same
    //    ruler. THIS hero's height is CONTENT-driven (1331px @1440×900 = 1.479·vh, but 1.212·vh @1200,
    //    and exactly 1.0·vh on tall viewports where it collapses to its 100svh floor) — so a vh-based
    //    amplitude yields a rate that slides from 0.189 to 0.28 across the viewport range, and demands
    //    more top bleed than the framing budget allows at 1440×1200 and 1024×768. Referencing the hero's
    //    own height makes the rate uniform 0.189 px/px EVERYWHERE by construction, and makes the bleed
    //    ratio (below) unit-free. Same tween, same trigger, same range, same ease, same scrub, same
    //    direction, same rest state (y=0) — only the ruler the amplitude is measured against.
    //    (This is also escape.cafe's own parameterisation: their hero image drifts ~25% of IMAGE height,
    //    not of viewport height — see design-reference/escape-hero-teardown.md:195.)
    //
    //    BLEED: the drift spends the media box's TOP headroom, so the box grew with it — .al-hero__media
    //    is now top:-20.4%/height:148% (desktop only), keeping the SAME 1.08× headroom-to-drift ratio and
    //    the SAME rest crop. The arithmetic lives in the al-hero.liquid CSS comment. hero.offsetHeight is
    //    layout (transform-independent) and re-read per refresh, so it tracks the box's % bleed exactly.
    if (img) {
      gsap.to(img, {
        y: () => CFG.imageDriftRate * hero.offsetHeight,
        ease: 'none',
        scrollTrigger: { ...heroRange(), scrub: CFG.scrub, invalidateOnRefresh: true },
      });
    }
    // 2. Headline (+ eyebrow — it rides inside .al-hero__headline-wrap) leads UP — RETIMED
    //    (EXIT DEAD-ZONE fix 2026-07-18). Was spread across the FULL hero exit (end 'bottom top' = the
    //    whole 1331px), so the locked −0.19·vh lead was only a ~0.128·vh differential — an invisible
    //    creep during the hold, with most of its travel wasted OFF-SCREEN after the wrap had already
    //    left. Now it completes when the WRAP EXITS THE VIEWPORT TOP: end = (wrap bottom in the layout)
    //    + the lead (which pulls it up early) = offsetTop+offsetHeight + headlineLead·vh. So the SAME
    //    locked −0.19·vh amplitude is spent across only the range the wrap is actually visible (~0.24·vh
    //    differential, ~2× the old), and it keeps rising the ENTIRE time it's on screen — reaching rest
    //    exactly as it flows off the top. ease:'none' (linear) so the rise is steady the whole way (a
    //    front-loaded ease finished it early and left it frozen-while-visible) and stays positionally
    //    deterministic (round-2 zero-jump). DELIBERATE DEVIATION from the literal "end at the pin
    //    release": at the shortened release (~324px) the wrap is still prominently on screen, so ending
    //    there would freeze a visible headline; ending at its exit keeps it in motion until the user
    //    has moved past it — the actual intent ("the text scrolls higher, then the user moves on").
    //    max(pinEnd) is a floor so the rise never ends before the button un-pins. Amplitude locked;
    //    only the trigger end + ease changed (timing retime, not a value change).
    if (headline) {
      const headlineExit = () => Math.max(pinEnd(), (headline.offsetTop + headline.offsetHeight) + CFG.headlineLead * vh());
      gsap.to(headline, {
        y: () => CFG.headlineLead * vh(),
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: () => '+=' + headlineExit(), scrub: CFG.scrub, invalidateOnRefresh: true },
      });
    }

    // 3. Footer parallax DOCK — RETIMED (score P2/OPEN-3): the band is FULLY FLUSH by the time its
    //    TOP crosses 50% of the viewport (not at hero exit). At rest the band keeps the 16px gap
    //    (F2) above .al-hero__content's bottom; the scrubbed translate closes it as the band rises
    //    from viewport-bottom to viewport-center. Travel = the MEASURED gap (content padding-bottom,
    //    re-read on refresh) — no hardcoded px. fromTo overrides the CSS static-flush transform:
    //    rest y:0 (gap baseline) → y:gap (flush). After flush it holds y:gap and scrolls normally.
    //    Trigger is the FOOTER (start 'top bottom' → end 'top center' = top at 50vh), a rigid unit.
    const content = hero.querySelector('.al-hero__content');
    if (footer && content) {
      gsap.fromTo(
        footer,
        { y: 0 },
        {
          y: () => parseFloat(getComputedStyle(content).paddingBottom) || 0,
          ease: 'none',
          scrollTrigger: {
            trigger: footer,
            start: 'top bottom',
            end: 'top 55%', // fully flush by the time the band top reaches 55vh → guaranteed flush BEFORE 50vh
            // scrub:true (position-exact), NOT CFG.scrub (time-lagged): the dock is GEOMETRY, so the
            // flush-by-50vh point must be deterministic, not smoothing-dependent. The move is 16px,
            // and Lenis already smooths the underlying scroll value, so it still reads smooth.
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
    }

    // 4. CTA — LOCKED: the decoupled right button HOLDS via a REAL GSAP PIN (the scoped exception —
    //    CLAUDE.md + al-hero-score.md P2; escape.cafe teardown Addendum A), scaling up 0.9→1.0 across
    //    the hold. GSAP pins the button (position:fixed under the hood) from the hero top, holding
    //    its screen position while the hero scrolls beneath, then cleanly un-pins so it scrolls away.
    //    pinSpacing:false — the button is absolute/out-of-flow, so no spacer enters the content column.
    if (ctaBtn) {
      // Pin END = pinEnd() (hoisted above) — the shopnow-top ↔ button-top alignment (new end rule). The
      // button rides DOWN to the shopnow row and releases there, then scrolls off tops-flush with it.
      // Clearance vs the footer is inherent (the shopnow block is structurally ABOVE the docked footer,
      // so releasing at its row lands the button above the cards — proven by the bbox sweep, not assumed).
      ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: () => '+=' + pinEnd(),
        pin: ctaBtn,
        pinSpacing: false,
        invalidateOnRefresh: true,
      });

      // SCALE-THROUGH-TRAVEL: 0.9 → 1.0 over the SAME range as the pin (full size exactly at release).
      // transform-origin 100% 50% (right-centre) glues it to the right gutter and keeps its visual
      // bottom at/above the layout bottom while small — so pinEnd()/clearance (offset-based,
      // transform-independent) are UNAFFECTED and clearance only ever gets more generous mid-hold.
      gsap.fromTo(
        ctaBtn,
        { scale: CTA_START_SCALE },
        {
          scale: 1,
          ease: 'none',
          transformOrigin: '100% 50%',
          scrollTrigger: { trigger: hero, start: 'top top', end: () => '+=' + pinEnd(), scrub: true, invalidateOnRefresh: true },
        }
      );
    }

    // 5. Hero footer cards — FADE in as they scroll into view (one-shot).
    //    Fade only (no y-transform): the card's `transform` is reserved for the CSS hover-scale
    //    (which has its own `transition: transform`). A `.from({y:34})` here fought that CSS
    //    transition AND a ScrollTrigger refresh re-applied the y:34 start state, leaving cards
    //    stuck 34px low — overhanging the flush row and pushing the hover image over the announce
    //    bar. A gsap.set + a plain TO tween on enter is refresh-proof and never touches the card's
    //    transform, so the cards stay flush (y:0) and the hover stays contained.
    if (cards.length) {
      gsap.set(cards, { autoAlpha: 0 });
      ScrollTrigger.create({
        trigger: footer,
        start: 'top 88%',
        once: true,
        onEnter: () =>
          gsap.to(cards, { autoAlpha: 1, duration: CFG.cardsDur, ease: 'power2.out', stagger: CFG.cardsStagger }),
      });
    }
  });

  // LATE-LAYOUT REFRESH — fixes the dock/pin going stale on a COLD (uncached) load. Two layout
  // changes land AFTER these triggers are created, and neither is covered by ScrollTrigger's
  // auto-refresh-on-'load':
  //   1. the giant Nohemi headline swaps in when the self-hosted webfont finishes (which can be
  //      after window 'load'), reflowing the hero and shifting the footer;
  //   2. Lenis loads async and sets `html.lenis body { height:auto }`, changing scroll metrics.
  // Either staled the dock's start/end so it never animated (the reported "footer never docks"
  // regression — invisible on a warm cache because the font is already loaded when triggers build).
  // Recompute all trigger bounds once fonts are ready, once on 'load', and once Lenis signals ready.
  // Warm-cache / no-Lenis paths: these are cheap no-ops (bounds already correct).
  const refreshST = () => ScrollTrigger.refresh();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshST);
  window.addEventListener('load', refreshST);
  window.addEventListener('al:lenis-ready', refreshST);

  // Triggers bound (desktop) — or the query didn't match (mobile/reduced-motion) and there is
  // intentionally nothing to bind. Either way, release the curtain's ScrollTrigger gate.
  resolveST();
})();
